import React, {useEffect, useRef, useState} from 'react';

import {AccountPageShell} from 'app/account/components/AccountPageShell';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {useTeams} from 'app/teams/api';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {ACCOUNT_SCIM_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  useCreateSCIMTeam,
  useDeleteSCIMTeam,
  useSCIMTeams,
  useUpdateSCIMTeam,
} from '../api';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {
  ListResponseType,
  SCIMTeamSerializedType,
  TeamSerializedType,
} from 'sweet-types';

export function SCIMPage(): JSX.Element {
  const {
    data: teams,
    error,
    isLoading,
  } = useTeams(convertLocationParamsToFetchParams({}));

  return (
    <AccountPageShell
      title={gt('SCIM')}
      location="scim"
      isLoading={isLoading}
      error={error}
      content={(teams?.count ?? 0) > 0 ? teams : undefined}
      errorContent={{head: gt('Could not load team information at the moment')}}
      noResultsContent={gt('No teams on your account.')}
    >
      {(teams) => (
        <>
          <p className="lead">
            {gt(
              'Here, you can ensure that your existing teams on your SweetProcess account are correctly mapped to their corresponding external teams. This section allows you to view the current mappings and create new SCIM external IDs where necessary, and next time your SCIM provider runs their sync it will pick up the mapping to the teams that you specify regardless of if the name matches.'
            )}
          </p>
          <TeamsTable teams={teams} />
        </>
      )}
    </AccountPageShell>
  );
}

type TableProps = {
  teams: ListResponseType<TeamSerializedType>;
};

const TeamsTable = ({teams}: TableProps) => {
  const {data: scimTeams} = useSCIMTeams();

  const getSCIMForTeam = (url: string): SCIMTeamSerializedType | undefined => {
    return scimTeams?.results.find((scimTeam) => scimTeam.team.url === url);
  };

  return (
    <table className="table table-hover">
      <thead>
        <tr>
          <th>Team Name</th>
          <th>SCIM External ID</th>
        </tr>
      </thead>
      <tbody>
        {teams.results?.map((team) => (
          <tr key={team.url}>
            <td>{team.name}</td>
            <td>
              <TeamExternalID team={team} scimTeam={getSCIMForTeam(team.url)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

type ExternalIDProps = {
  team: TeamSerializedType;
  scimTeam?: SCIMTeamSerializedType;
};

const TeamExternalID = ({team, scimTeam}: ExternalIDProps) => {
  const [newExternalID, setNewExternalID] = useState<string>(
    scimTeam?.scim_external_id ?? ''
  );
  const [editState, setEditState] = useState<
    'editing' | 'creating' | 'viewing'
  >('viewing');

  const [createSCIMTeam] = useCreateSCIMTeam();
  const [deleteSCIMTeam] = useDeleteSCIMTeam();
  const [updateSCIMTeam] = useUpdateSCIMTeam();

  function actionsGetter(): MenuItemsType {
    return [
      {
        text: gt('Edit External ID'),
        key: 'edit-id',
        icon: 'fa fa-pen',
        condition: () => scimTeam?.scim_external_id != null,
        callback: () => setEditState('editing'),
      },
      {
        text: gt('Add External ID'),
        key: 'create-id',
        icon: 'fa fa-pen',
        condition: () => scimTeam?.scim_external_id == null,
        callback: () => setEditState('creating'),
      },
      {
        text: gt('Remove External ID'),
        key: 'remove-id',
        icon: 'far fa-trash',
        condition: () => scimTeam?.url != null,
        callback: () =>
          scimTeam != null
            ? deleteSCIMTeam({url: scimTeam?.url, name: scimTeam?.team.name})
            : null,
      },
    ];
  }

  const handleCreateSCIMTeam = (team: TeamSerializedType) => {
    createSCIMTeam({
      team: team.url,
      scim_external_id: newExternalID,
    });
    setNewExternalID('');
    setEditState('viewing');
  };

  const handleUpdateSCIMTeam = (
    team: TeamSerializedType,
    scimTeam?: SCIMTeamSerializedType
  ) => {
    if (scimTeam != null) {
      updateSCIMTeam({
        url: scimTeam.url,
        body: {scim_external_id: newExternalID, team: team.url},
      });
      setNewExternalID('');
    }
    setEditState('viewing');
  };

  return (
    <div className="d-flex justify-content-between align-items-center">
      {editState === 'editing' ? (
        <ExternalIdInput
          team={team}
          scimTeam={scimTeam}
          onSave={handleUpdateSCIMTeam}
          onCancel={() => setEditState('viewing')}
          newExternalID={newExternalID}
          setNewExternalID={setNewExternalID}
        />
      ) : editState === 'creating' ? (
        <ExternalIdInput
          team={team}
          scimTeam={scimTeam}
          onSave={handleCreateSCIMTeam}
          onCancel={() => setEditState('viewing')}
          newExternalID={newExternalID}
          setNewExternalID={setNewExternalID}
        />
      ) : (
        <>
          <span>{scimTeam?.scim_external_id ?? ''}</span>
          <ThreeDotsDropdownMenu actionsGetter={actionsGetter} object={team} />
        </>
      )}
    </div>
  );
};

interface InputProps {
  onSave: (
    team: TeamSerializedType,
    scimTeam?: SCIMTeamSerializedType
  ) => unknown;
  onCancel: () => unknown;
  newExternalID: string;
  setNewExternalID: (string) => unknown;
  team: TeamSerializedType;
  scimTeam?: SCIMTeamSerializedType;
}

export const ExternalIdInput = ({
  team,
  scimTeam,
  onSave,
  onCancel,
  newExternalID,
  setNewExternalID,
}: InputProps) => {
  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSave(team, scimTeam);
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current != null) {
      ref.current.focus();
    }
  }, []);

  return (
    <div className="input-group">
      <input
        ref={ref}
        type="text"
        className="form-control"
        value={newExternalID}
        onChange={(e) => setNewExternalID(e.target.value)}
        placeholder="Enter External ID"
        onKeyPress={onKeyPress}
      />
      <div className="input-group-append">
        <button className="btn btn-secondary" onClick={onCancel}>
          {gt('Cancel')}
        </button>
        <button
          className="btn btn-primary"
          onClick={() => onSave(team, scimTeam)}
        >
          {gt('Save')}
        </button>
      </div>
    </div>
  );
};

export const SCIMBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    text={gt('SCIM')}
    link={ACCOUNT_SCIM_URL}
  />
);
